import { Alert, Typography } from '@/ui';

import type { MomentsSavedHighlight } from '@/store/api/momentsApi';

import { SavedArtifactNotesList } from '@/pages/MemberReflectionArtifact/MemberReflectionArtifactView';
import { legacyStoredSourceIdentity } from '@/utils/artifactNoteIdentity';

export interface SavedNoteSnapshotsViewProps {
  notes: MomentsSavedHighlight[];
  status: 'loading' | 'ready' | 'error';
  busy?: boolean;
  onUpdate: (id: string, note: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

// Normal owner notes, not a Keep-only collection or a feature-gated creation
// surface. The container must supply authenticated owner results; denied source
// metadata remains null. Never place snapshots on body coordinates here.
export const SavedNoteSnapshotsView = ({
  busy,
  notes,
  onDelete,
  onUpdate,
  status,
}: SavedNoteSnapshotsViewProps) => (
  <section
    aria-label="Saved note snapshots"
    className="fs-mask"
    data-dd-privacy="mask"
  >
    <Typography variant="h2">Saved notes</Typography>
    <Typography variant="body2">
      Your saved passages and private notes. A snapshot may not match the
      current reflection.
    </Typography>
    {status === 'loading' ? <p role="status">Loading saved notes…</p> : null}
    {status === 'error' ? (
      <Alert severity="error">
        We couldn’t load saved notes. Please try again.
      </Alert>
    ) : null}
    {status === 'ready' && notes.length === 0 ? (
      <p>No saved notes in this view.</p>
    ) : null}
    {status === 'ready'
      ? notes.map((note) => (
          <section
            key={note.id}
            aria-label={`Saved note ${note.id}`}
            id={`saved-note-${note.id}`}
          >
            <Typography variant="body2">
              {note.artifact
                ? 'Saved passage (snapshot)'
                : 'Source unavailable • saved passage'}
            </Typography>
            <Typography variant="body2">
              {note.sourceIdentity
                ? 'Unlocated snapshot'
                : 'Original source version unknown'}
            </Typography>
            <blockquote style={{ margin: '12px 0', whiteSpace: 'pre-wrap' }}>
              {note.selectedText}
            </blockquote>
            <SavedArtifactNotesList
              deleteDisabled={busy}
              disabled={busy}
              notes={[
                {
                  ...note,
                  sourceIdentity: legacyStoredSourceIdentity(
                    note.sourceIdentity,
                  ),
                },
              ]}
              alwaysShowActions
              onDelete={onDelete}
              onUpdate={onUpdate}
            />
          </section>
        ))
      : null}
  </section>
);
